import {
  Checkbox,
  Frequency,
  FrequencyValue,
  Input,
  RadioGroup,
  TimeSpan,
  validateCheckbox,
  validateFrequency,
  validateRadioGroup,
  ValidationFunction,
} from "basis";
import { formatAmount } from "library/moneyUtils";

type Props = {
  data: {
    creditLimit: number | undefined;
    maxCreditLimit: number | undefined;
  };
};

export const nzValidateNewCreditLimit: (
  validateData: Props,
) => ValidationFunction<typeof Input> = (validateData) => (value, _props) =>
  validateNewCreditLimit(value, validateData);

export function validateNewCreditLimit(value, data): string | null {
  const error = Input.DEFAULT_PROPS.validate(value, data);

  if (error !== null) {
    return error;
  }

  const newCreditLimit = Number(value);

  if (isNaN(newCreditLimit)) {
    return "Only 0-9 are allowed";
  }

  if (newCreditLimit % 100 !== 0) {
    return "Must be a multiple of $100";
  }

  const {
    data: { creditLimit, maxCreditLimit, newCreditLimit: approvedCreditLimit },
  } = data;

  if (newCreditLimit - creditLimit < 100) {
    return `Must be at least $100 greater than your current credit limit of ${formatAmount(
      creditLimit,
    )}`;
  }

  if (newCreditLimit > maxCreditLimit) {
    return `Must be at most ${formatAmount(maxCreditLimit)}`;
  }

  if (newCreditLimit > approvedCreditLimit) {
    return `Must be at most ${formatAmount(approvedCreditLimit)}`;
  }

  return null;
}

export function nzValidateRequiredField(
  value: string,
  {
    isEmpty,
  }: {
    isEmpty: (value) => boolean;
  },
) {
  if (isEmpty(value)) {
    return "Required";
  }
  return null;
}

export function nzValidateTimeSpanField(value: {
  years: string;
  months: string;
}) {
  const { years, months } = value;
  if (years === "" && months === "") {
    return "Required";
  }
  if (isNaN(Number(years)) || isNaN(Number(months))) {
    return "Only 0-9 are allowed";
  }
  const error = TimeSpan.DEFAULT_PROPS.validate(value);
  if (error !== null) {
    return error;
  }
  return null;
}

export function validateFrequencyFieldFormV1(value: FrequencyValue) {
  if (value.amount === "") {
    return "Required";
  }
  const frequencyValue = Number(value.amount);
  if (isNaN(frequencyValue)) {
    return "Only 0-9 are allowed";
  }
  return null;
}

export const nzValidateRadioGroupField: ValidationFunction<typeof RadioGroup> =
  (value, props) => {
    if (value === "") {
      return "Required";
    }
    const errors = validateRadioGroup(value, props);
    if (errors) {
      return errors;
    }
    return null;
  };

export const validateFrequencyField: ValidationFunction<typeof Frequency> = (
  value,
  props,
) => {
  if (value.amount === "") {
    return {
      amount: "Required",
    };
  }
  const frequencyValue = Number(value.amount);
  if (isNaN(frequencyValue)) {
    return {
      amount: "Only 0-9 are allowed",
    };
  }

  const error = validateFrequency(value, props);
  if (error !== null) {
    return error;
  }

  return null;
};

export const nzValidateCheckboxField: ValidationFunction<typeof Checkbox> = (
  value,
  props,
) => {
  if (!value) {
    return "Required";
  }
  const errors = validateCheckbox(value, props);
  if (errors) {
    return errors;
  }
  return null;
};
